import { Button, Card, CardContent, PageHeader, StatusChip, TextareaField } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForceClearTable, useStaffState } from "../../features/ops/queries.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/tables/$tableSessionId")({
  component: TableDetailScreen,
});

function TableDetailScreen() {
  const { tableSessionId } = Route.useParams();
  const { data } = useStaffState();
  const clear = useForceClearTable();
  const [reason, setReason] = useState("Party left; staff verified table is clear.");
  const table = data?.tables.find((item) => item.id === tableSessionId);
  if (!table) return <PageHeader title="Table detail" subtitle="Loading table…" />;

  // FR-STAFF-021: staff coordinating service for a table need to see its
  // active orders and pending gifts — but never exchanged contact details
  // (FR-STAFF-026). Orders carry a table session id directly; gifts carry
  // sender/recipient table labels (no contact/identity fields either way).
  const tableOrders = (data?.orders ?? []).filter((order) => order.tableSessionId === table.id);
  const tableGifts = (data?.gifts ?? []).filter(
    (gift) => gift.senderTableLabel === table.label || gift.recipientTableLabel === table.label,
  );

  return (
    <div className="space-y-6">
      <PageHeader title={table.label} subtitle={`${table.code} · capacity ${table.capacity}`} />
      <Card>
        <CardContent className="space-y-4 p-5">
          <StatusChip tone={table.status === "occupied" ? "warning" : "success"}>
            {table.status}
          </StatusChip>
          <p>Party size: {table.partySize}</p>
          <p>Social active: {table.socialActive ? "Yes" : "No"}</p>
          <p className="rounded-md bg-muted p-3 text-sm">
            Force clear creates an audit-style mock entry and removes social visibility. It does not
            expose guest identities.
          </p>
          <TextareaField
            id="clearReason"
            label="Force-clear reason"
            value={reason}
            onChange={setReason}
          />
          <Button
            variant="destructive"
            onClick={() => clear.mutate({ tableSessionId: table.id, reason })}
          >
            Force-clear table
          </Button>
          {table.auditNote ? (
            <p className="text-muted-foreground text-sm">Last audit note: {table.auditNote}</p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="font-display font-semibold text-lg">Active orders / ออเดอร์ที่โต๊ะนี้</h2>
          {tableOrders.length === 0 ? (
            <p className="text-muted-foreground text-sm">No orders for this table.</p>
          ) : (
            <ul className="space-y-2">
              {tableOrders.map((order) => (
                <li
                  key={order.id}
                  className="flex items-center justify-between rounded-md border border-border p-3 text-sm"
                >
                  <span>
                    #{order.id} ·{" "}
                    {order.lines.map((line) => `${line.qty}× ${line.name}`).join(", ")}
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusChip tone="neutral">{order.status}</StatusChip>
                    {formatMoney(order.totalMinor)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="font-display font-semibold text-lg">
            Gifts involving this table / ของขวัญที่เกี่ยวข้อง
          </h2>
          {tableGifts.length === 0 ? (
            <p className="text-muted-foreground text-sm">No gifts for this table.</p>
          ) : (
            <ul className="space-y-2">
              {tableGifts.map((gift) => (
                <li
                  key={gift.id}
                  className="flex items-center justify-between rounded-md border border-border p-3 text-sm"
                >
                  <span>
                    {gift.senderTableLabel} → {gift.recipientTableLabel}
                  </span>
                  <StatusChip tone="info">{gift.status}</StatusChip>
                </li>
              ))}
            </ul>
          )}
          <p className="text-muted-foreground text-xs">
            No customer contact details are ever surfaced here (PRIV-*, FR-STAFF-026).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
